import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/motion/variants";
import { TradingPartnersTrustBar } from "@/components/TrustBar";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { TradingTrustStrip } from '@/components/TradingTrustStrip';
import { 
  Ship, 
  Shield, 
  FileText, 
  CreditCard, 
  CheckCircle, 
  Building2, 
  FileCheck, 
  Banknote,
  PackageOpen,
  Clock,
  MapPin,
  Mail,
  Calendar,
  TrendingDown,
  TrendingUp,
  Settings,
  Container,
  FlaskConical,
  Truck,
  FileWarning,
  Zap,
  Globe,
  Users
} from "lucide-react";

/**
 * BaseOil Page
 * Trading page for LC-backed base oil (Group I/II) exports from Asia ports to Africa
 * Business logic: Converts FOB Asia to CFR Africa using transferable LCs,
 * standardized inspection scope, and containerized flexitank execution with monthly program aggregation
 */
const BaseOil = () => {
  return (
    <div className="min-h-screen pt-6">
      <SEO
        title="LC-Backed Base Oil to Africa | CFR Trading | Asia to Kenya, Tanzania, Uganda | Padmora"
        description="Convert FOB Asia base oil into CFR Africa with transferable LCs, standardized inspection, and containerized flexi execution. Group I/II base oils (150N/500N) in monthly programs for better pricing."
        image="/og-trading.jpg"
        canonical="https://padmora.com/trading/base-oil"
        keywords={[
          "base oil trading",
          "base oil Africa",
          "base oil Kenya",
          "base oil Tanzania",
          "base oil Uganda",
          "CFR Africa base oil",
          "150N base oil",
          "500N base oil",
          "Group I base oil",
          "Group II base oil",
          "Asia base oil exports",
          "LC-backed base oil trading",
          "flexitank base oil",
          "Kenya base oil import",
          "Tanzania base oil import",
          "Uganda base oil import",
          "Asia base oil",
          "transferable LC base oil",
          "monthly base oil program"
        ]}
        aiOptimization={{
          aiKeywords: [
            "automated base oil logistics",
            "standardized inspection scope",
            "containerized lubricant trading",
            "programmatic CFR base oil",
            "LC documentation automation",
            "monthly aggregation programs"
          ],
          entities: [
            "Base Oil",
            "150N",
            "500N",
            "Group I",
            "Group II",
            "Africa",
            "Kenya",
            "Tanzania",
            "Uganda",
            "Asia",
            "Flexitank"
          ],
          topics: [
            "Lubricant Trading",
            "Base Oil Export",
            "Africa Imports",
            "Asia Exports",
            "Documentary Credits",
            "Containerized Shipping",
            "Monthly Programs"
          ]
        }}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Base Oil Trading Service",
          "description": "LC-backed base oil trading from Asia ports to Africa with standardized documentation and flexitank execution in monthly programs.",
          "provider": {
            "@type": "Organization",
            "name": "Padmora"
          },
          "serviceType": "Commodity Trading",
          "areaServed": [
            {
              "@type": "Country",
              "name": "Kenya"
            },
            {
              "@type": "Country",
              "name": "Tanzania"
            },
            {
              "@type": "Country",
              "name": "Uganda"
            }
          ],
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Trade Type",
              "value": "CFR Containerized"
            },
            {
              "@type": "PropertyValue",
              "name": "Payment Method",
              "value": "Transferable Letter of Credit"
            },
            {
              "@type": "PropertyValue",
              "name": "Program Type",
              "value": "Monthly Aggregation"
            },
            {
              "@type": "PropertyValue",
              "name": "Origin",
              "value": "Asia Ports"
            }
          ]
        }}
        breadcrumbs={[
          { name: "Home", url: "https://padmora.com/" },
          { name: "Trading", url: "https://padmora.com/trading" },
          { name: "Base Oil → Africa", url: "https://padmora.com/trading/base-oil" }
        ]}
      />
      <Navigation />

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col justify-center">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-4xl md:text-6xl font-bold text-slate-900 mb-6"
            >
              LC-backed Base Oil to Africa, done right
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-8"
            >
              We convert FOB Asia into CFR Africa, using transferable/confirmed LCs, standardized inspection, and containerized flexi execution. Monthly programs for better pricing through aggregation.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com?subject=CFR%20Quote%20Request%20-%20Base%20Oil"
                  rel="noopener noreferrer"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get CFR Quote
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                  className="text-slate-700 hover:text-slate-900"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>

                    <TradingTrustStrip />

          {/* Partners */}
          <div className="mt-16">
            <TradingPartnersTrustBar />
          </div>
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto"
          >
            {/* Problem */}
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-red-100 p-8 rounded-lg shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Small volumes = high costs & unreliable supply
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Individual buyers get hit with premium freight rates and inconsistent availability. We aggregate monthly programs to secure better pricing and reliable supply chains.
              </p>
            </motion.div>

            {/* Solution */}
            <motion.div
              variants={fadeInUp}
              className="bg-white border border-green-100 p-8 rounded-lg shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Monthly aggregation programs
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Multiple buyers, single program. Better pricing through volume aggregation, standardized LCs, and consistent Asia sourcing with flexitank execution.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* What we ship */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              What we ship
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 text-center mb-12 max-w-3xl mx-auto"
            >
              20' GP flexitank (~22 MT nominal), ex-Asia ports to CFR Africa
            </motion.p>
            
            {/* Core Products */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">150N Base Oil</h3>
                <p className="text-sm text-slate-600 mb-3">Group II (28-33 cSt @ 40°C)</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Engine oil base, hydraulic fluids</p>
                  <p>• ~22 MT per 20' GP</p>
                  <p>• Standard inspection + COA</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">500N Base Oil</h3>
                <p className="text-sm text-slate-600 mb-3">Group II (90-110 cSt @ 40°C)</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• Heavy-duty engine oils, gear oils</p>
                  <p>• Marine & metalworking applications</p>
                  <p>• High viscosity index (100-115)</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">220N Base Oil</h3>
                <p className="text-sm text-slate-600 mb-3">Group II (40-45 cSt @ 40°C)</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• General-purpose engine oils</p>
                  <p>• Industrial applications</p>
                  <p>• Core grade for blending</p>
                </div>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Group II Base Oil</h3>
                <p className="text-sm text-slate-600 mb-3">Hydrocracked & hydrotreated</p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p>• &lt; 0.03% sulfur, &gt; 90% saturates</p>
                  <p>• VI 90-120, clear & bright</p>
                  <p>• Global standard for modern lubes</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Additional Products */}
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">100N Base Oil</h3>
                <p className="text-sm text-slate-600">Light viscosity Group II (28-33 cSt @ 40°C) for engine oil base</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">Bright Stock</h3>
                <p className="text-sm text-slate-600">High viscosity Group I (400-500 cSt @ 40°C) for viscosity boosters in blends</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-slate-50 border border-slate-200 p-6 rounded-lg"
              >
                <h3 className="text-lg font-semibold mb-2">Custom Blends</h3>
                <p className="text-sm text-slate-600">Tailored specifications for specific end-use applications</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              How it works
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {/* Step 1 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">1</span>
                  </div>
                  <h3 className="text-lg font-semibold">Join monthly program</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Multiple buyers aggregate into single monthly program for better pricing and guaranteed allocation.
                </p>
              </motion.div>

              {/* Step 2 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">2</span>
                  </div>
                  <h3 className="text-lg font-semibold">LC + Asia sourcing</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Transferable LC issued to Padmora. We secure Asia supply and hold flexitank allocations.
                </p>
              </motion.div>

              {/* Step 3 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">3</span>
                  </div>
                  <h3 className="text-lg font-semibold">Load & ship</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Standard inspection + COA, food-grade flexis, clean BL on board, and present uniform docs.
                </p>
              </motion.div>

              {/* Step 4 */}
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg shadow-sm"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600">4</span>
                  </div>
                  <h3 className="text-lg font-semibold">CFR delivery</h3>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Delivery to Africa ports, buyer clears under applicable import certification, and we reconcile proceeds.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Monthly Program Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Monthly program benefits
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <TrendingDown className="h-6 w-6 text-green-600" />
                  <h3 className="text-lg font-semibold">Better Pricing</h3>
                </div>
                <p className="text-slate-600 text-sm">
                  Volume aggregation reduces per-unit costs for all participants in the monthly program.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-6 w-6 text-blue-600" />
                  <h3 className="text-lg font-semibold">Predictable Supply</h3>
                </div>
                <p className="text-slate-600 text-sm">
                  Monthly allocation guarantees ensure consistent supply timing and reduce planning uncertainty.
                </p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-purple-600" />
                  <h3 className="text-lg font-semibold">Risk Sharing</h3>
                </div>
                <p className="text-slate-600 text-sm">
                  Shared program reduces individual buyer risk and provides collective bargaining power.
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* LC & Docs */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              LC & docs we accept
            </motion.h2>
            
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Letter of Credit Card */}
              <motion.div variants={fadeInUp} className="bg-white border border-border p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Letter of Credit</h3>
                </div>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">UCP600</span> compliant</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Transferable</span>, partials allowed</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Transshipment</span> permitted</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">±10%</span> qty/amount tolerance</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">21-day</span> presentation period</span>
                  </p>
                </div>
              </motion.div>

              {/* Required Documents Card */}
              <motion.div variants={fadeInUp} className="bg-white border border-border p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Required Documents</h3>
                </div>
                <div className="space-y-2 text-slate-600">
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">3/3 OBL</span> to order</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Commercial Invoice</span></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Packing List</span></span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Inspection Certificate</span> + COA</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Import Certification</span> (Kenya/Tanzania/Uganda)</span>
                  </p>
                  <p className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-1 flex-shrink-0" />
                    <span><span className="font-medium">Insurance cert</span> (if CIF/add-on)</span>
                  </p>
                </div>
              </motion.div>
            </motion.div>

            {/* Assignment of proceeds - Full width below */}
            <motion.div variants={fadeInUp} className="mt-6 bg-slate-50 border border-slate-200 p-4 rounded-lg">
              <p className="text-sm text-slate-600 text-center">
                <span className="font-medium">Assignment of proceeds</span> permitted to pay nominated vendors on presentation (optional line to ease bank ops)
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Compliance & Inspection */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-4"
            >
              Standardized inspection, zero surprises
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 text-center mb-12"
            >
              We fix the inspection wording once and run it every lot. Typical parameters for base oil and flexi loading, with retain samples, CQ/COQ at load.
            </motion.p>
            
            <motion.div
              variants={fadeInUp}
              className="bg-card border border-border p-8 rounded-lg"
            >
              <div className="flex items-center gap-3 mb-6">
                <FlaskConical className="h-6 w-6 text-blue-600" />
                <h3 className="text-xl font-semibold">Standard Inspection Scope</h3>
              </div>
              <div className="grid md:grid-cols-2 gap-6 text-slate-600">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Load Parameters</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Quantity verification (weight/volume)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Flexitank integrity & cleanliness</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Container seal & temperature</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Retain samples (sealed, labeled)</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Quality Tests (COA)</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Viscosity (40°C, 100°C)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Flash point, pour point</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Color, appearance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>Per agreed product specification</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-sm text-slate-700">
                  <FileWarning className="inline h-4 w-4 text-blue-600 mr-2" />
                  <span className="font-medium">See our specimen scope</span> for complete structure and parameter limits
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Ops & Lead Times */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-6xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Ops & lead times
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg"
              >
                <Globe className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Origin</h3>
                <p className="text-slate-600 text-sm">Asia ports (Malaysia, Thailand, Vietnam)</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg"
              >
                <Container className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Packaging</h3>
                <p className="text-slate-600 text-sm">20' GP, flexitank ~22 MT nominal</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg"
              >
                <Clock className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Lead Time</h3>
                <p className="text-slate-600 text-sm">Monthly program allocation; 2–3 weeks to booking; 8–12 days transit</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg"
              >
                <Truck className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="font-semibold mb-2">Payment Flows</h3>
                <p className="text-slate-600 text-sm">LC at sight/usance; vendor D+10/D+15 via assignment</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Insurance */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Insurance (CFR + add-on or CIF)
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 gap-8"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-blue-600" />
                  <h3 className="text-xl font-semibold">Default (CFR)</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Optional ICC(A) + SRCC/war add-on</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Certificates issued per BL</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Claims support provided</span>
                  </li>
                </ul>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-card border border-border p-6 rounded-lg"
              >
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-green-600" />
                  <h3 className="text-xl font-semibold">CIF (if required)</h3>
                </div>
                <ul className="space-y-2 text-slate-600 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>110% sum insured (invoice value)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Loss payee: issuing/confirming bank</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Full claims assistance & documentation</span>
                  </li>
                </ul>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-center mb-12"
            >
              Who it's for
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-6"
            >
              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Building2 className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Africa Lubricant Blenders</h3>
                <p className="text-sm text-slate-600">Manufacturers needing consistent base oil supply for blending operations</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Distributors & Traders</h3>
                <p className="text-sm text-slate-600">Needing bankable LC-to-execution coverage with clean documentation</p>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="bg-white border border-border p-6 rounded-lg text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Monthly Program Participants</h3>
                <p className="text-sm text-slate-600">Small to medium buyers wanting better pricing through volume aggregation</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold mb-6"
            >
              Why us (outcomes)
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-xl text-slate-600 mb-12"
            >
              Volume aggregation. Uniform text. Vendor direct-pay. Hard space first. Clean presentations. You get product on time instead of excuses.
            </motion.p>
            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-6 gap-4"
            >
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <Users className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Volume aggregation</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Uniform text</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <CreditCard className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Vendor direct-pay</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <Ship className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Hard space first</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <CheckCircle className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">Clean presentations</p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-card border border-border p-4 rounded-lg">
                <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium">On-time delivery</p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="max-w-2xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-3xl font-bold text-slate-900 mb-6"
            >
              Ready to move base oil to Africa?
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="text-slate-600 mb-8"
            >
              Get a CFR quote, review our LC draft template, or schedule a 15-minute call to discuss your monthly program requirements.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="default"
                className="px-8 py-4 text-lg bg-primary hover:bg-primary/90 text-white"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com?subject=CFR%20Quote%20Request%20-%20Base%20Oil"
                  rel="noopener noreferrer"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Get CFR Quote
                </a>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="px-8 py-4 text-lg"
                asChild
              >
                <a
                  href="mailto:michel@padmora.com"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  Send enquiry
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BaseOil;
